import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Volume2, VolumeX, Play, Pause } from 'lucide-react';

// Video Background Component with Cloudinary Support
export const VideoBackground = ({ 
  videoUrl, 
  fallbackImage, 
  overlay = true,
  autoPlay = true,
  loop = true,
  muted = true,
  showControls = false,
  className = '' 
}) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(muted);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  // If no video URL or error, show fallback image
  if (!videoUrl || hasError) {
    return (
      <div className={`absolute inset-0 ${className}`}>
        <img 
          src={fallbackImage} 
          alt="" 
          className="w-full h-full object-cover"
        />
        {overlay && (
          <>
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/60" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      {/* Fallback image while video loads */}
      {!isLoaded && fallbackImage && (
        <img 
          src={fallbackImage} 
          alt="" 
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {/* Video Element */}
      <video
        ref={videoRef}
        autoPlay={autoPlay}
        loop={loop}
        muted={isMuted}
        playsInline
        preload="auto"
        onLoadedData={() => setIsLoaded(true)}
        onError={() => setHasError(true)}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <source src={videoUrl} type="video/mp4" />
      </video>

      {/* Overlays */}
      {overlay && (
        <>
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
          {/* Scan line effect */}
          <div className="absolute inset-0 bg-[repeating-linear-gradient(0deg,transparent,transparent_2px,rgba(0,0,0,0.03)_2px,rgba(0,0,0,0.03)_4px)] pointer-events-none" />
        </>
      )}

      {/* Video Controls */}
      {showControls && isLoaded && (
        <motion.div 
          className="absolute bottom-6 right-6 flex items-center gap-3 z-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
        >
          <button
            onClick={togglePlay}
            className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:border-vav-gold transition-colors"
            data-testid="video-play-toggle"
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button
            onClick={toggleMute}
            className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:border-vav-gold transition-colors"
            data-testid="video-mute-toggle"
          >
            {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        </motion.div>
      )}
    </div>
  );
};

// Inline Video Player for Cards
export const VideoCard = ({ 
  videoUrl, 
  posterImage, 
  title,
  className = '' 
}) => {
  const videoRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (videoRef.current) {
      if (isHovered) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isHovered]);

  return (
    <div 
      className={`relative overflow-hidden ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Poster Image */}
      <img 
        src={posterImage} 
        alt={title} 
        className={`w-full h-full object-cover transition-opacity duration-500 ${
          isHovered && videoUrl ? 'opacity-0' : 'opacity-100'
        }`}
      />

      {/* Video */}
      {videoUrl && (
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          preload="metadata"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <source src={videoUrl} type="video/mp4" />
        </video>
      )}

      {/* Play indicator */}
      {videoUrl && !isHovered && (
        <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center">
          <Play className="w-4 h-4 text-white fill-white" />
        </div>
      )}
    </div>
  );
};

export default VideoBackground;
